import { useCallback, useEffect, useRef, useState } from 'react'
import {
  cacheBackendConsent,
  canReloadAfterFailedWithdrawal,
  clearPendingDecision,
  ConsentApiError,
  createConsentDecision,
  invalidateBackendConsentCache,
  loadConsentContext,
  markConsentChecked,
  readPendingDecision,
  saveBackendConsent,
  stopWithdrawnPurposes,
  storePendingDecision,
  wasConsentCheckedThisSession,
} from './backendPersistence'
import type { ConsentContext, ConsentDecision } from '../../lib/consent/backendContract'
import type { ConsentPurpose, ConsentState } from './types'
import { continueWithoutOptionalCookies, readConsentState } from './deviceState'
import { clearUngrantedPurposeIdentifiers } from './clearWithdrawnIdentifiers'
import { reloadPage } from './reloadPage'

export type ConsentBackendError = 'unavailable' | 'conflict' | 'unconfirmed'

export function useConsentBackend(
  locale: string,
  onLoaded: (saved?: ConsentState, pending?: ConsentDecision) => void,
  onConflict: (purposes: Array<ConsentPurpose>) => void,
) {
  const backendEnabled = typeof window !== 'undefined' && !!window.__LE_CONSENT_BACKEND__
  // A choice this tab already confirmed is not re-read on every full page load.
  const [isAlreadyChecked] = useState(() => backendEnabled && wasConsentCheckedThisSession(readConsentState()))
  const [context, setContext] = useState<ConsentContext>()
  const [isSaving, setIsSaving] = useState(false)
  const saving = useRef(false)
  const [isLoadingBackend, setIsLoadingBackend] = useState(backendEnabled && !isAlreadyChecked)
  const [error, setError] = useState<ConsentBackendError>()

  useEffect(() => {
    if (!backendEnabled || isAlreadyChecked) return
    const controller = new AbortController()
    loadConsentContext(controller.signal)
      .then((nextContext) => {
        if (controller.signal.aborted) return
        setContext(nextContext)
        const pending = readPendingDecision()
        if (pending) {
          continueWithoutOptionalCookies()
          onLoaded(undefined, pending)
          setError('unconfirmed')
          return
        }
        const previous = readConsentState()
        const saved = previous?.receipt ? cacheBackendConsent(nextContext, nextContext.state, previous.receipt, previous.purposes) : undefined
        if (!saved) {
          invalidateBackendConsentCache()
          onLoaded()
          if (previous?.purposes.length && canReloadAfterFailedWithdrawal()) reloadPage()
          return
        }
        if (saved.receipt) markConsentChecked(saved.receipt.revision)
        onLoaded(saved)
        const previousPurposes = previous?.purposes ?? []
        if (saved.purposes.length !== previousPurposes.length || saved.purposes.some(purpose => !previousPurposes.includes(purpose))) reloadPage()
      })
      .catch((error) => {
        if (controller.signal.aborted) return
        setError('unavailable')
        if (error instanceof ConsentApiError && [401, 409].includes(error.status)) {
          const previous = readConsentState()
          invalidateBackendConsentCache()
          onLoaded()
          if (previous?.purposes.length && canReloadAfterFailedWithdrawal()) reloadPage()
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingBackend(false)
      })
    return () => controller.abort()
  }, [backendEnabled, isAlreadyChecked, onLoaded])

  const save = useCallback(
    async(nextPurposes: Array<ConsentPurpose>, method: ConsentDecision['method']) => {
      if (saving.current) return false
      saving.current = true
      setIsSaving(true)
      setError(undefined)
      const previous = readConsentState()
      const withdrawing = previous?.purposes.some((purpose) => !nextPurposes.includes(purpose))
      stopWithdrawnPurposes(nextPurposes)
      let submittedKey: string | undefined
      try {
        const currentContext = context ?? (await loadConsentContext())
        setContext(currentContext)
        const pending = readPendingDecision()
        // A retry reuses the exact evidence and key, even after a lost response/reload.
        const sameChoice =
          pending &&
          (pending.method === method || method === 'CONFIGURE') &&
          pending.consents.every((consent) => consent.granted === nextPurposes.includes(consent.purpose_key))
        const decision = sameChoice
          ? pending
          : await createConsentDecision(currentContext, nextPurposes, method, locale)
        submittedKey = decision.idempotency_key
        if (!storePendingDecision(decision)) throw new ConsentApiError(503)
        continueWithoutOptionalCookies()
        clearUngrantedPurposeIdentifiers([])
        const saved = await saveBackendConsent(currentContext, decision)
        if (!saved) throw new ConsentApiError(503)
        return true
      } catch(error) {
        setError('unavailable')
        if (error instanceof ConsentApiError && [401, 409].includes(error.status)) {
          if (readPendingDecision()?.idempotency_key === submittedKey) clearPendingDecision()
          invalidateBackendConsentCache()
          // Never silently replay a stale grant against a newer revision/catalogue.
          setError('conflict')
          try {
            const refreshed = await loadConsentContext()
            setContext(refreshed)
            onConflict(
              refreshed.state?.consents
                .filter(
                  (consent) =>
                    consent.granted &&
                    refreshed.purposes.some(
                      (purpose) => purpose.key === consent.purpose_key && purpose.version === consent.purpose_version,
                    ),
                )
                .map((consent) => consent.purpose_key) ?? [],
            )
          } catch {
            setContext(undefined)
          }
        }
        if (withdrawing && canReloadAfterFailedWithdrawal()) reloadPage()
        return false
      } finally {
        saving.current = false
        setIsSaving(false)
      }
    },
    [context, locale, onConflict],
  )

  return { enabled: backendEnabled, busy: isSaving || isLoadingBackend, error, save }
}
